import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ShoppingCart, CheckCircle2, Sparkles, HelpCircle, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import GalleryModal from '../../components/GalleryModal';
import SEO from '../../components/SEO';

export default function ProduktEn() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const productImages = [
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/danshaker-3-farver.png',
      alt: 'Danshaker Sorting Machine - Available in multiple custom colors',
      caption: 'Choose your favorite color freely – shown here is just a small sample of possibilities'
    },
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/c4a731ab-6721-435d-8de5-d6368af70f19.jpg',
      alt: 'Danshaker Sorting Machine - Tool Holder',
      caption: 'Tool holder with rake is an available accessory'
    },
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/8dbd0056-687d-4231-9ce4-f8f186646ff9.jpg',
      alt: 'Danshaker with rubber brake and black front wheels',
      caption: 'Danshaker rubber brake and heavy-duty black front wheels'
    },
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/80ade798-7838-4838-ba9d-88d0e973e725.jpg',
      alt: 'Danshaker Sorting Machine - Ergonomic Design',
      caption: 'Compact, balanced, and ergonomic design'
    },
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/e78bab06-df3e-49dd-b2cd-e5a4891479a9.jpg',
      alt: 'Danshaker Sorting Machine - Robust Construction',
      caption: 'Danish quality engineered for demanding stable environments'
    },
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/a91119ee-cb50-4bb0-8636-9ff6a6aa87e2.jpg',
      alt: 'Danshaker Sorting Machine in custom color',
      caption: 'Prefer vibrant custom colors? We accommodate any RAL finish upon request'
    },
    {
      url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/735ab98a-19ad-4a44-8706-12f21749ddce.jpg',
      alt: 'Danshaker Sorting Machine - Clean Bedding Discharge',
      caption: 'Effortless dumping and minimal loss of clean bedding material'
    }
  ];

  useEffect(() => {
    async function loadData() {
      const galleriData = await fetchCollection('galleri', 'en');
      if (galleriData && galleriData.length > 0) {
        setGalleryImages(galleriData[0].images || []);
      }
    }
    loadData();
  }, []);

  const openGallery = (index) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const didYouKnow = [
    'The Danshaker runs on a rechargeable battery and manages up to 86 stall cleanings of approx. 10 minutes each.',
    'The Danshaker is easy to steer and maneuver, intuitive to operate, and quick to clean.',
    'Engineered and manufactured in Denmark from heavy-duty components built to withstand tough equestrian environments.',
    'Multiple retrofit accessories are available so you can customize the machine to your unique facility needs.',
    'Choose your preferred custom RAL color for your machine – from classic blue to vibrant custom finishes.',
    'Optional glitter metallic finish can be added for a subtle custom aesthetic.'
  ];

  const steps = [
    {
      img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/82c2389c-2509-456f-8e9c-aa5c44a15f3e.jpg',
      title: 'Shovel bedding into hopper',
      desc: '– quick and effortless'
    },
    {
      img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/69ce56ce-163d-4516-8f4f-6b6ab388a8f4.jpg',
      title: 'Danshaker sorts for you',
      desc: '– bedding cleaned in seconds'
    },
    {
      img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/6043796d-21d0-49bc-ab4e-1aa2283c1f84.jpg',
      title: 'Reuse clean bedding',
      desc: '– dispose of manure only'
    }
  ];

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Danshaker Sorting Machine',
    image: productImages.map((img) => img.url),
    description: 'Original Danish sorting machine for horse stalls. Halves mucking time and bedding consumption.',
    brand: {
      '@type': 'Brand',
      name: 'Danshaker'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://danshaker.dk/en/product',
      priceCurrency: 'EUR',
      price: '4225',
      priceValidUntil: '2027-12-31',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition'
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Danshaker Sorting Machine - Efficient Stall Cleaning"
        description="The original Danshaker stable sorting machine. Halve your bedding costs, save up to 50% time, and prevent repetitive physical strain. Book a free demo."
        path="/en/product"
        lang="en"
        schema={productSchema}
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Main Product Showcase Section */}
      <section className="mx-auto max-w-7xl px-4 pt-12 pb-16 md:px-8 md:pt-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          
          {/* Product Gallery (Left) */}
          <div className="lg:col-span-6 space-y-4">
            <div 
              onClick={() => {
                setGalleryImages(productImages.map(img => img.url));
                openGallery(activeImageIndex);
              }}
              className="group relative overflow-hidden rounded-2xl border-2 border-slate-300 bg-white p-2 shadow-xl cursor-pointer"
            >
              <img
                src={productImages[activeImageIndex].url}
                alt={productImages[activeImageIndex].alt}
                width="800"
                height="500"
                fetchPriority="high"
                decoding="async"
                className="w-full rounded-xl object-cover h-[300px] sm:h-[450px] md:h-[500px] transition-all duration-300 group-hover:scale-[1.01]"
              />
              
              {/* Overlay controls */}
              <div className="absolute inset-x-4 top-1/2 flex -translate-y-1/2 items-center justify-between pointer-events-none">
                <button
                  onClick={prevImage}
                  className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-lg border border-slate-200 transition hover:bg-white hover:text-[#0E67A2] cursor-pointer"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6" aria-hidden="true" />
                </button>
                <button
                  onClick={nextImage}
                  className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-lg border border-slate-200 transition hover:bg-white hover:text-[#0E67A2] cursor-pointer"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-lg bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-xs">
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Click to enlarge</span>
              </div>
            </div>

            {productImages[activeImageIndex].caption && (
              <p className="text-center text-xs text-slate-500 font-medium">
                {productImages[activeImageIndex].caption}
              </p>
            )}

            {/* Thumbnail selector */}
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {productImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className={`overflow-hidden rounded-xl border-2 transition ${
                    activeImageIndex === idx ? 'border-[#0E67A2] shadow-sm' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                  aria-label={`Show image ${idx + 1}`}
                >
                  <img
                    src={img.url}
                    alt={img.alt}
                    width="120"
                    height="120"
                    loading="lazy"
                    decoding="async"
                    className="h-16 w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details & Specs */}
          <div className="lg:col-span-6 space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#0E67A2]/20 bg-[#0E67A2]/5 px-4 py-1.5 text-xs font-bold text-[#0E67A2] mb-3">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>The original Danish sorting machine</span>
              </div>
              <h1 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                Danshaker Sorting Machine
              </h1>
              <p className="mt-2 text-[#0E67A2] font-black text-lg">
                Efficient, ergonomic, and cost-effective stable revolution
              </p>
            </div>

            <p className="text-slate-700 leading-relaxed font-medium">
              Danshaker is specially developed for horse stables to remove heavy manual mucking work. The machine vibrates the stall bedding free of clean reusable material while capturing droppings and manure directly into the collector.
            </p>

            <p className="text-slate-700 leading-relaxed font-medium">
              The machine has a clearance height of 69 cm, making a standard wheelbarrow fit nicely underneath.
            </p>

            {/* Price & Primary CTA */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 pb-4">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Standard price</span>
                  <div className="text-3xl font-black text-slate-900">
                    Contact for quote
                  </div>
                </div>
                <span className="text-xs font-bold text-[#0E67A2] bg-[#0E67A2]/10 px-3 py-1 rounded-full">
                  Leasing & Rental options available
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link
                  to="/en/free-demo"
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3.5 font-bold text-white shadow-md transition hover:bg-[#0E67A2]/90"
                >
                  <Calendar className="h-5 w-5" aria-hidden="true" />
                  Book free demo
                </Link>
                <Link
                  to="/en/contact"
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3.5 font-bold text-slate-700 transition hover:bg-slate-50"
                >
                  Contact sales
                </Link>
              </div>
            </div>

            {/* Value checklist */}
            <div className="space-y-3 pt-2 border-t border-slate-200">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Key Benefits</h3>
              <ul className="grid gap-2 sm:grid-cols-2 text-sm text-slate-700 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0E67A2] shrink-0" />
                  <span>Up to 50% time saved</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0E67A2] shrink-0" />
                  <span>Up to 50% bedding reduction</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0E67A2] shrink-0" />
                  <span>Significantly less manure volume</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0E67A2] shrink-0" />
                  <span>Cordless battery operation</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0E67A2] shrink-0" />
                  <span>Adjustable vibration speed</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0E67A2] shrink-0" />
                  <span>1-minute wash stall cleaning</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Did You Know Section */}
      <section className="border-y border-slate-200 bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Did you know that...</h2>
            <p className="mt-2 text-slate-600 font-medium">
              Important facts about how Danshaker delivers convenience in daily stable operations.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {didYouKnow.map((item, idx) => (
              <div key={idx} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-xs">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#0E67A2]/10 text-[#0E67A2] font-black">
                  {idx + 1}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed font-medium">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works 3-step section */}
      <section className="py-20 mx-auto max-w-7xl px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">How it works in practice</h2>
          <p className="mt-3 text-slate-600 font-medium">
            Muck out in 3 simple steps – fast, gentle, and effortless.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {steps.map((st, idx) => (
            <div key={idx} className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
              <div className="aspect-4/3 w-full overflow-hidden bg-slate-100">
                <img
                  src={st.img}
                  alt={st.title}
                  width="400"
                  height="300"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="text-xs font-bold text-[#0E67A2] uppercase tracking-wider mb-1">Step {idx + 1}</div>
                <h3 className="text-lg font-bold text-slate-900">{st.title}</h3>
                <p className="text-sm text-slate-600 font-medium mt-1">{st.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Accessories Banner Link */}
      <section className="bg-slate-900 py-16 text-white text-center">
        <div className="mx-auto max-w-4xl px-4 md:px-8 space-y-6">
          <h2 className="text-3xl font-extrabold sm:text-4xl">
            Upgrade your Danshaker with original accessories
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto font-medium">
            From wheelbarrow mounts and tool holders to custom colors and arena levelers.
          </p>
          <div>
            <Link
              to="/en/accessories"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0E67A2] px-8 py-3.5 font-bold text-white shadow-lg transition hover:bg-[#0E67A2]/90"
            >
              Explore accessories
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryModal
        isOpen={isGalleryOpen}
        images={productImages.map((p) => p.url)}
        currentIndex={galleryIndex}
        onClose={() => setIsGalleryOpen(false)}
        onPrev={() => setGalleryIndex((prev) => (prev - 1 + productImages.length) % productImages.length)}
        onNext={() => setGalleryIndex((prev) => (prev + 1) % productImages.length)}
      />
    </div>
  );
}
